import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  CloudQueue as CloudIcon,
} from '@mui/icons-material';
import { useConfig } from '../contexts/ConfigContext';

const ConfigurationPage = () => {
  const navigate = useNavigate();
  const { config, saveConfig, validateConfig, isConfigured } = useConfig();
  
  const [formData, setFormData] = useState({
    apiGatewayUrl: '',
    cognitoUserPoolId: '',
    cognitoClientId: '',
    region: '',
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (config) {
      setFormData(config);
    }
  }, [config]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.apiGatewayUrl.trim()) {
      newErrors.apiGatewayUrl = 'API Gateway URL is required';
    } else {
      try {
        new URL(formData.apiGatewayUrl);
      } catch {
        newErrors.apiGatewayUrl = 'Please enter a valid URL';
      }
    }
    
    if (!formData.cognitoUserPoolId.trim()) {
      newErrors.cognitoUserPoolId = 'Cognito User Pool ID is required';
    }
    
    if (!formData.cognitoClientId.trim()) {
      newErrors.cognitoClientId = 'Cognito Client ID is required';
    }
    
    if (!formData.region.trim()) {
      newErrors.region = 'AWS Region is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.region)) {
      newErrors.region = 'Please enter a valid AWS region (e.g., us-east-1)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setMessage('');
    
    try {
      validateConfig(formData);
      saveConfig(formData);
      setMessage('Configuration saved successfully!');
      
      // Redirect to login after successful configuration
      setTimeout(() => {
        navigate('/login');
      }, 1500);
      
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <SettingsIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            System Configuration
          </Typography>
        </Box>
        
        <Typography variant="body1" color="text.secondary" paragraph>
          Configure your AWS resources to connect the AI Recruitment System to your backend services.
        </Typography>

        {!isConfigured && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Please configure your AWS resources before using the application.
          </Alert>
        )}

        <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <CloudIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Quick Setup</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Use the default configuration from your deployed CloudFormation stack:
            </Typography>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Gateway URL"
                value={formData.apiGatewayUrl}
                onChange={handleInputChange('apiGatewayUrl')}
                error={!!errors.apiGatewayUrl}
                helperText={errors.apiGatewayUrl || 'The base URL of your API Gateway (e.g., https://api123.execute-api.us-east-1.amazonaws.com)'}
                placeholder="https://your-api-id.execute-api.region.amazonaws.com"
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cognito User Pool ID"
                value={formData.cognitoUserPoolId}
                onChange={handleInputChange('cognitoUserPoolId')}
                error={!!errors.cognitoUserPoolId}
                helperText={errors.cognitoUserPoolId || 'Your Cognito User Pool ID (e.g., us-east-1_ABC123)'}
                placeholder="us-east-1_ABC123"
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cognito Client ID"
                value={formData.cognitoClientId}
                onChange={handleInputChange('cognitoClientId')}
                error={!!errors.cognitoClientId}
                helperText={errors.cognitoClientId || 'Your Cognito App Client ID'}
                placeholder="1a2b3c4d5e6f7g8h9i0j"
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="AWS Region"
                value={formData.region}
                onChange={handleInputChange('region')}
                error={!!errors.region}
                helperText={errors.region || 'AWS region where your resources are deployed'}
                placeholder="us-east-1"
                required
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {message && (
            <Alert 
              severity={message.includes('successfully') ? 'success' : 'error'} 
              sx={{ mb: 2 }}
            >
              {message}
            </Alert>
          )}

          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
              size="large"
            >
              {loading ? 'Saving...' : 'Save Configuration'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default ConfigurationPage;
